import type { CarnivalGameState, CarnivalViewerState } from "../types";

/**
 * The only hidden information in this game is the *content* of a pending
 * (not-yet-revealed) selection for this round — dice, revealed sins, hand
 * counts, and score history are all public per the rulebook ("remembering
 * played sins is not a sin"). Each player's exact remaining hand is kept
 * private to themselves (others only see the count) so the client doesn't
 * do the memory work the physical game intends for players to do.
 */
export function filterStateForViewer(
  state: CarnivalGameState,
  viewerPlayerId: string | null,
): CarnivalViewerState {
  const handCountsByPlayerId: Record<string, number> = {};
  for (const player of state.players) {
    handCountsByPlayerId[player.playerId] =
      state.handsByPlayerId[player.playerId]?.length ?? 0;
  }

  const playersWhoSelectedThisRound = state.players
    .map((player) => player.playerId)
    .filter((playerId) => state.selectionsByPlayerId[playerId] != null);

  return {
    gameId: state.gameId,
    phase: state.phase,
    round: state.round,
    players: state.players,
    seatingOrder: state.seatingOrder,
    devilCardHolderId: state.devilCardHolderId,
    revealOrder: state.revealOrder,
    nextToRevealIndex: state.nextToRevealIndex,
    dice: state.dice,
    handCountsByPlayerId,
    ownHand: viewerPlayerId
      ? (state.handsByPlayerId[viewerPlayerId] ?? [])
      : [],
    ownPendingSelection: viewerPlayerId
      ? (state.selectionsByPlayerId[viewerPlayerId] ?? null)
      : null,
    playersWhoSelectedThisRound,
    revealedThisRound: state.revealedThisRound,
    scoreHistoryByPlayerId: state.scoreHistoryByPlayerId,
    lastRoundSummary: state.lastRoundSummary,
    winnerIds: state.winnerIds,
  };
}

export function buildViewerStates(
  state: CarnivalGameState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, CarnivalViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
